import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPlans,
  fetchMySubscription,
  startCheckout,
  cancelMySubscription,
  resumeMySubscription,
  openBillingPortal,
  applyCoupon,
} from "../../store/thunks/subscriptionThunk";
import { clearCoupon } from "../../store/slices/subscriptionSlice";
import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import { colors } from "../../utils/Colors";

const c = colors.color;

const PLAN_ICONS = ["🚀", "⚡", "💎", "👑", "🔥", "🌟"];
const PLAN_GRADIENTS = [
  `linear-gradient(135deg, ${c.secondary}, #00a67e)`,
  `linear-gradient(135deg, #446EE7, #6a4fdb)`,
  `linear-gradient(135deg, #f1b44c, #f46a6a)`,
  `linear-gradient(135deg, #7b2ff7, #f107a3)`,
];

const SubscriptionPlans = () => {
  const dispatch = useDispatch();
  const { plans, mySubscription, checkout, action, coupon } = useSelector((s) => s.subscription);

  // Coupon local state
  const [couponInput, setCouponInput] = useState("");
  const [selectedPlanForCoupon, setSelectedPlanForCoupon] = useState(null);

  useEffect(() => {
    dispatch(fetchPlans());
    dispatch(fetchMySubscription());
    return () => { dispatch(clearCoupon()); };
  }, [dispatch]);

  const activeSub = mySubscription.data;

  const handleApplyCoupon = (plan) => {
    if (!couponInput.trim()) return;
    setSelectedPlanForCoupon(plan._id);
    dispatch(applyCoupon({ couponCode: couponInput.trim(), subscriptionPlanId: plan._id }));
  };

  const handleClearCoupon = () => {
    setCouponInput("");
    setSelectedPlanForCoupon(null);
    dispatch(clearCoupon());
  };

  const handleCheckout = (plan) => {
    const couponApplied =
      coupon.data?.valid && selectedPlanForCoupon === plan._id
        ? couponInput.trim()
        : null;
    dispatch(startCheckout({ planId: plan._id, couponCode: couponApplied }));
  };

  return (
    <>
      <Header />

      <style>{`
        @media (max-width: 1024px) {
          .subscription-plans-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            max-width: 640px !important;
          }
        }
        @media (max-width: 560px) {
          .subscription-plans-grid {
            grid-template-columns: 1fr !important;
            max-width: 360px !important;
          }
        }
      `}</style>

      <div style={{ background: "#f7fffe", minHeight: "100vh" }}>
        {/* ── Hero Section ── */}
        <div
          style={{
            background: `linear-gradient(135deg, ${c.secondary}18, #00a67e10, #fff)`,
            borderBottom: `1px solid ${c.lightGrey}`,
            padding: "60px 0 50px",
            textAlign: "center",
          }}
        >
          <div className="container">
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: `${c.secondary}18`,
                border: `1px solid ${c.secondary}40`,
                borderRadius: 30,
                padding: "6px 18px",
                marginBottom: 20,
              }}
            >
              <span style={{ fontSize: 14, color: c.secondary, fontWeight: 600 }}>
                💳 Subscription Plans
              </span>
            </div>

            <h1
              style={{
                fontSize: "clamp(28px, 5vw, 46px)",
                fontWeight: 800,
                color: c.primary,
                lineHeight: 1.2,
                marginBottom: 16,
              }}
            >
              Choose Your{" "}
              <span
                style={{
                  color: c.secondary,
                  position: "relative",
                }}
              >
                Employer Plan
              </span>
            </h1>

            <p
              style={{
                fontSize: 17,
                color: c.darkGrey,
                maxWidth: 520,
                margin: "0 auto 12px",
                lineHeight: 1.6,
              }}
            >
              Post jobs and find the right candidates.{" "}
              <strong style={{ color: c.primary }}>
                Your first job post is always free.
              </strong>
            </p>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                marginTop: 8,
              }}
            >
              {["No hidden fees", "Cancel anytime", "Stripe secured"].map((t) => (
                <span
                  key={t}
                  style={{
                    fontSize: 12,
                    color: c.darkGrey,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span style={{ color: c.secondary }}>✓</span> {t}
                  {t !== "Stripe secured" && (
                    <span style={{ marginLeft: 6, color: c.lightGrey }}>•</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="container" style={{ padding: "48px 16px 80px" }}>

          {/* ── Active Subscription Banner ── */}
          {activeSub && (
            <div
              style={{
                background: "#fff",
                border: `2px solid ${c.secondary}`,
                borderRadius: 16,
                padding: "20px 24px",
                marginBottom: 40,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 16,
                boxShadow: `0 4px 20px ${c.secondary}22`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: `linear-gradient(135deg, ${c.secondary}, #00a67e)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                    flexShrink: 0,
                  }}
                >
                  ⚡
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 16, color: c.primary }}>
                      {activeSub.planId?.name}
                    </span>
                    <span
                      style={{
                        background: `${c.secondary}18`,
                        color: c.secondary,
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "2px 10px",
                        borderRadius: 30,
                        border: `1px solid ${c.secondary}40`,
                      }}
                    >
                      ACTIVE
                    </span>
                    {activeSub.cancelAtPeriodEnd && (
                      <span
                        style={{
                          background: "#FFE8E8",
                          color: "#f46a6a",
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "2px 10px",
                          borderRadius: 30,
                        }}
                      >
                        CANCELING SOON
                      </span>
                    )}
                  </div>

                  {/* Job quota bar */}
                  <div style={{ marginTop: 8 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 4,
                      }}
                    >
                      <span style={{ fontSize: 12, color: c.darkGrey }}>
                        Job posts used this month
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color:
                            activeSub.jobsUsedThisPeriod >= activeSub.jobsLimit
                              ? "#f46a6a"
                              : c.primary,
                        }}
                      >
                        {activeSub.jobsUsedThisPeriod} / {activeSub.jobsLimit}
                      </span>
                    </div>
                    <div
                      style={{
                        width: "100%",
                        maxWidth: 260,
                        height: 6,
                        borderRadius: 3,
                        background: c.lightGrey,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${Math.min(
                            100,
                            ((activeSub.jobsUsedThisPeriod || 0) /
                              (activeSub.jobsLimit || 1)) *
                              100
                          )}%`,
                          background:
                            activeSub.jobsUsedThisPeriod >= activeSub.jobsLimit
                              ? "#f46a6a"
                              : `linear-gradient(90deg, ${c.secondary}, #00a67e)`,
                          borderRadius: 3,
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {activeSub.cancelAtPeriodEnd ? (
                  <button
                    disabled={action.loading}
                    onClick={() =>
                      dispatch(resumeMySubscription()).then(() =>
                        dispatch(fetchMySubscription())
                      )
                    }
                    style={{
                      padding: "9px 20px",
                      borderRadius: 10,
                      border: "none",
                      background: `linear-gradient(135deg, ${c.secondary}, #00a67e)`,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: action.loading ? "not-allowed" : "pointer",
                      opacity: action.loading ? 0.7 : 1,
                      boxShadow: `0 4px 12px ${c.secondary}44`,
                    }}
                  >
                    ↺ Resume Plan
                  </button>
                ) : (
                  <button
                    disabled={action.loading}
                    onClick={() =>
                      dispatch(cancelMySubscription()).then(() =>
                        dispatch(fetchMySubscription())
                      )
                    }
                    style={{
                      padding: "9px 20px",
                      borderRadius: 10,
                      border: "1.5px solid #f46a6a",
                      background: "transparent",
                      color: "#f46a6a",
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: action.loading ? "not-allowed" : "pointer",
                      opacity: action.loading ? 0.7 : 1,
                    }}
                  >
                    Cancel Plan
                  </button>
                )}
                <button
                  onClick={() => dispatch(openBillingPortal())}
                  style={{
                    padding: "9px 20px",
                    borderRadius: 10,
                    border: `1.5px solid ${c.lightGrey}`,
                    background: "#fff",
                    color: c.primary,
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                >
                  🧾 Billing Portal
                </button>
              </div>
            </div>
          )}

          {/* ── Plans Grid ── */}
          {plans.loading ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  border: `4px solid ${c.lightGrey}`,
                  borderTop: `4px solid ${c.secondary}`,
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>              <p style={{ color: c.darkGrey, fontSize: 15 }}>Loading plans...</p>
            </div>
          ) : plans.data.length === 0 ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>📋</div>
              <h4 style={{ color: c.primary }}>No plans available yet</h4>
              <p style={{ color: c.darkGrey }}>Check back soon!</p>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${plans.data.length}, minmax(0, 1fr))`,
                gap: 24,
                maxWidth: Math.min(plans.data.length * 300, 1400),
                margin: "0 auto",
              }}
              className="subscription-plans-grid"
            >
              {plans.data.map((plan, idx) => {
                const isFreePlan = !plan.price || Number(plan.price) === 0;
                const isCurrent =
                  activeSub?.planId?._id === plan._id ||
                  activeSub?.planId === plan._id ||
                  // If the user has no active subscription, the free plan is the default/current one
                  (!activeSub && isFreePlan);
                const gradient =
                  PLAN_GRADIENTS[idx % PLAN_GRADIENTS.length];
                const icon = PLAN_ICONS[idx % PLAN_ICONS.length];

                const features = [
                  isFreePlan
                    ? "1st job free"
                    : `${plan.jobsLimit} additional job${plan.jobsLimit !== 1 ? "s" : ""}`,
                  "Access to all candidates",
                  "Email notifications",
                  "Candidate messaging",
                  ...(plan.jobsLimit >= 10 ? ["Priority listing"] : []),
                  ...(plan.jobsLimit >= 20 ? ["Dedicated support"] : []),
                ];

                return (
                  <div
                    key={plan._id}
                    style={{
                      background: "#fff",
                      borderRadius: 20,
                      overflow: "hidden",
                      boxShadow: isCurrent
                        ? `0 8px 32px ${c.secondary}33`
                        : "0 4px 20px rgba(0,0,0,0.08)",
                      border: isCurrent
                        ? `2px solid ${c.secondary}`
                        : "2px solid transparent",
                      position: "relative",
                      transition: "transform 0.25s, box-shadow 0.25s",
                      cursor: "default",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-6px)";
                      e.currentTarget.style.boxShadow = isCurrent
                        ? `0 16px 40px ${c.secondary}44`
                        : "0 12px 36px rgba(0,0,0,0.14)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = isCurrent
                        ? `0 8px 32px ${c.secondary}33`
                        : "0 4px 20px rgba(0,0,0,0.08)";
                    }}
                  >
                    {/* Current plan badge */}
                    {isCurrent && (
                      <div
                        style={{
                          position: "absolute",
                          top: 16,
                          right: 16,
                          background: `linear-gradient(135deg, ${c.secondary}, #00a67e)`,
                          color: "#fff",
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "4px 12px",
                          borderRadius: 30,
                          letterSpacing: "0.5px",
                          zIndex: 1,
                        }}
                      >
                        ✓ CURRENT PLAN
                      </div>
                    )}

                    {/* Gradient top header */}
                    <div
                      style={{
                        background: gradient,
                        padding: "28px 28px 24px",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {/* decorative circles */}
                      <div
                        style={{
                          position: "absolute",
                          top: -20,
                          right: -20,
                          width: 100,
                          height: 100,
                          borderRadius: "50%",
                          background: "rgba(255,255,255,0.1)",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: -30,
                          right: 30,
                          width: 70,
                          height: 70,
                          borderRadius: "50%",
                          background: "rgba(255,255,255,0.08)",
                        }}
                      />

                      <div style={{ fontSize: 36, marginBottom: 10 }}>{icon}</div>
                      <h3
                        style={{
                          color: "#fff",
                          fontWeight: 800,
                          fontSize: 22,
                          margin: 0,
                        }}
                      >
                        {plan.name}
                      </h3>
                      {plan.description && (
                        <p
                          style={{
                            color: "rgba(255,255,255,0.8)",
                            fontSize: 13,
                            margin: "6px 0 0",
                            lineHeight: 1.4,
                          }}
                        >
                          {plan.description}
                        </p>
                      )}

                      {/* Price */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          gap: 4,
                          marginTop: 18,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 42,
                            fontWeight: 900,
                            color: "#fff",
                            lineHeight: 1,
                          }}
                        >
                          ${plan.price}
                        </span>
                        <span
                          style={{
                            color: "rgba(255,255,255,0.75)",
                            fontSize: 15,
                            marginBottom: 4,
                          }}
                        >
                          / month
                        </span>
                      </div>
                    </div>

                    {/* Features */}
                    <div style={{ padding: "24px 28px" }}>
                      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px" }}>
                        {features.map((f) => (
                          <li
                            key={f}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "8px 0",
                              borderBottom: `1px solid ${c.lightGrey}55`,
                              fontSize: 14,
                              color: c.primary,
                            }}
                          >
                            <span
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                background: `${c.secondary}18`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                flexShrink: 0,
                                color: c.secondary,
                                fontWeight: 700,
                              }}
                            >
                              ✓
                            </span>
                            {f}
                          </li>
                        ))}
                      </ul>

                      {/* CTA Button */}
                      {isCurrent ? (
                        <button
                          disabled
                          style={{
                            width: "100%",
                            padding: "13px",
                            borderRadius: 12,
                            border: `2px solid ${c.secondary}`,
                            background: `${c.secondary}10`,
                            color: c.secondary,
                            fontWeight: 700,
                            fontSize: 15,
                            cursor: "not-allowed",
                            letterSpacing: "0.3px",
                          }}
                        >
                          ✓ Current Plan
                        </button>
                      ) : isFreePlan ? (
                        <button
                          disabled
                          style={{
                            width: "100%",
                            padding: "13px",
                            borderRadius: 12,
                            border: `2px solid ${c.lightGrey}`,
                            background: "#fff",
                            color: c.darkGrey,
                            fontWeight: 700,
                            fontSize: 15,
                            cursor: "not-allowed",
                            letterSpacing: "0.3px",
                          }}
                        >
                          Free Plan
                        </button>
                      ) : (
                        <>
                          {/* ── Coupon Section ── */}
                          {/* <div style={{ marginBottom: 14 }}>
                            {!(coupon.data?.valid && selectedPlanForCoupon === plan._id) && (
                              <div style={{ display: "flex", gap: 8 }}>
                                <input
                                  value={selectedPlanForCoupon === plan._id || !selectedPlanForCoupon ? couponInput : ""}
                                  onChange={(e) => {
                                    setCouponInput(e.target.value.toUpperCase());
                                    if (selectedPlanForCoupon !== plan._id) {
                                      dispatch(clearCoupon());
                                      setSelectedPlanForCoupon(null);
                                    }
                                  }}
                                  onFocus={() => {
                                    if (selectedPlanForCoupon !== plan._id) {
                                      dispatch(clearCoupon());
                                      setSelectedPlanForCoupon(null);
                                    }
                                  }}
                                  placeholder="Coupon code"
                                  style={{
                                    flex: 1,
                                    padding: "9px 12px",
                                    borderRadius: 10,
                                    border: `1.5px solid ${
                                      coupon.error && selectedPlanForCoupon === plan._id
                                        ? "#f46a6a"
                                        : c.lightGrey
                                    }`,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    letterSpacing: "0.5px",
                                    outline: "none",
                                    color: c.primary,
                                  }}
                                />
                                <button
                                  onClick={() => handleApplyCoupon(plan)}
                                  disabled={coupon.loading && selectedPlanForCoupon === plan._id}
                                  style={{
                                    padding: "9px 14px",
                                    borderRadius: 10,
                                    border: `1.5px solid ${c.secondary}`,
                                    background: "transparent",
                                    color: c.secondary,
                                    fontWeight: 700,
                                    fontSize: 13,
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {coupon.loading && selectedPlanForCoupon === plan._id ? "..." : "Apply"}
                                </button>
                              </div>
                            )}

                            {coupon.error && selectedPlanForCoupon === plan._id && (
                              <p style={{ color: "#f46a6a", fontSize: 12, margin: "6px 0 0", fontWeight: 500 }}>
                                ✕ {coupon.error}
                              </p>
                            )}

                            {coupon.data?.valid && selectedPlanForCoupon === plan._id && (
                              <div
                                style={{
                                  background: `${c.secondary}0d`,
                                  border: `1.5px solid ${c.secondary}40`,
                                  borderRadius: 10,
                                  padding: "12px 14px",
                                  fontSize: 13,
                                }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                  <span style={{ color: c.darkGrey }}>Original Price</span>
                                  <span style={{ color: c.primary, fontWeight: 600 }}>${coupon.data.originalPrice}</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                  <span style={{ color: "#27ae60" }}>
                                    🏷️ Coupon ({coupon.data.discountValue}% off)
                                  </span>
                                  <span style={{ color: "#27ae60", fontWeight: 700 }}>−${coupon.data.discountAmount}</span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    paddingTop: 6,
                                    borderTop: `1px solid ${c.secondary}30`,
                                    marginTop: 4,
                                  }}
                                >
                                  <span style={{ color: c.primary, fontWeight: 700 }}>Final Price</span>
                                  <span style={{ color: c.secondary, fontWeight: 800, fontSize: 15 }}>${coupon.data.finalPrice}</span>
                                </div>
                                <button
                                  onClick={handleClearCoupon}
                                  style={{
                                    marginTop: 8,
                                    background: "none",
                                    border: "none",
                                    color: "#f46a6a",
                                    fontSize: 12,
                                    cursor: "pointer",
                                    padding: 0,
                                    fontWeight: 500,
                                  }}
                                >
                                  ✕ Remove coupon
                                </button>
                              </div>
                            )}
                          </div> */}

                          <button
                            disabled={checkout.loading}
                            onClick={() => handleCheckout(plan)}
                            style={{
                              width: "100%",
                              padding: "13px",
                              borderRadius: 12,
                              border: "none",
                              background: checkout.loading ? c.lightGrey : gradient,
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: 15,
                              cursor: checkout.loading ? "not-allowed" : "pointer",
                              letterSpacing: "0.3px",
                              boxShadow: checkout.loading ? "none" : "0 4px 16px rgba(0,0,0,0.18)",
                              transition: "opacity 0.2s",
                            }}
                            onMouseEnter={(e) => { if (!checkout.loading) e.currentTarget.style.opacity = "0.88"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                          >
                            {checkout.loading ? "Redirecting..." : activeSub ? "⇄ Switch to this Plan" : "Get Started →"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Trust Bar ── */}
          <div
            style={{
              marginTop: 60,
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "16px 40px",
            }}
          >
            {[
              { icon: "🔒", text: "Secured by Stripe" },
              { icon: "↩️", text: "Cancel anytime" },
              { icon: "📧", text: "Email support included" },
              { icon: "⚡", text: "Instant activation" },
            ].map((t) => (
              <div
                key={t.text}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  color: c.darkGrey,
                }}
              >
                <span>{t.icon}</span>
                <span>{t.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default SubscriptionPlans;